import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography } from "@mhome/ui";
import AddIcon from "@mui/icons-material/Add";
import GroupFilter from "./group-filter";
import EditGroupDialog from "./edit-group-dialog";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import {
  deleteGroup,
  listGroups,
  saveGroup,
} from "../service/integration-service";
import { useToast } from "../common/toast";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import { useWebSocket } from "../ws/websocket";

const CreateGroupCard = ({ onCreateClick }) => {
  return (
    <Card
      elevation={0}
      className="flex items-center justify-center h-[90px] text-white transition-all duration-200 select-none cursor-pointer"
      style={{
        backgroundColor: "hsl(var(--primary))",
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
      }}
      onClick={onCreateClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
      }}
    >
      <CardContent className="p-4 text-center">
        <AddIcon className="mb-1" />
        <Typography
          variant="subtitle2"
          className="font-medium text-sm leading-tight"
        >
          Create Group
        </Typography>
      </CardContent>
    </Card>
  );
};

const GroupCard = ({ group, index, getComponentBgColor, onEditClick }) => {
  return (
    <Card
      elevation={0}
      className="h-[90px] border-none cursor-pointer transition-all duration-200 select-none"
      style={{
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
      }}
      onClick={() => onEditClick(group)}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor =
          getComponentBgColor("hsl(var(--card))");
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
      }}
    >
      <CardContent className="p-4 h-full flex flex-col justify-center">
        <Typography
          variant="subtitle2"
          noWrap
          className="font-medium text-sm text-center leading-tight"
        >
          {group.name || `Group ${index + 1}`}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          noWrap
          className="mt-1 text-center"
        >
          {group.entities?.length || 0} entities
        </Typography>
      </CardContent>
    </Card>
  );
};

const GroupListView = ({
  entityType,
  groupData,
  entityData,
  showFilter,
  onGroupChanged,
}) => {
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [groupFilterText, setGroupFilterText] = useState("");
  const [groupSortBy, setGroupSortBy] = useState("alphabet");
  const { successToast, errorToast } = useToast();
  const { sendAndWaitWs } = useWebSocket();
  const searchParams = new URLSearchParams(window.location.search);
  const integrationId = searchParams.get("id");

  useEffect(() => {
    console.log("groupData", groupData);
  }, []);

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  const handleLoadGroups = async () => {
    const resp = await listGroups(sendAndWaitWs, integrationId);
    if (resp) {
      onGroupChanged(resp.groups);
    } else {
      errorToast("Error!");
    }
  };

  const handleEditGroup = (group = null) => {
    setSelectedGroup(group);
    setNewGroup(!group);
    setGroupName(group ? group.name : "");
    setSelectedEntities(
      group ? group.entities.map((e) => `${e.integration}-${e.id}`) || [] : []
    );
    setGroupDialogOpen(true);
  };

  const handleToggleEntity = (entityId) => {
    setSelectedEntities((prev) =>
      prev.includes(entityId)
        ? prev.filter((id) => id !== entityId)
        : [...prev, entityId]
    );
  };

  const handleSaveGroup = async (updatedSelection) => {
    const group = {
      groupId: null,
      entityType: entityType,
      name: groupName,
      integration: integrationId,
      entities: [],
    };
    if (selectedGroup) {
      group.groupId = selectedGroup.groupId;
    }
    updatedSelection.forEach((s) => {
      const integrationId = s.substring(0, s.indexOf("-"));
      const entityId = s.substring(s.indexOf("-") + 1);
      group.entities.push({ id: entityId, integration: integrationId });
    });

    if (!group.name) {
      errorToast("Name is required!");
      return;
    }
    if (group.entities.length === 0) {
      errorToast("No group members are selected!");
      return;
    }

    const resp = await saveGroup(sendAndWaitWs, group);
    if (resp) {
      if (resp.success) {
        successToast("Saved");
        handleLoadGroups();
        setGroupDialogOpen(false);
        setGroupName("");
        setSelectedEntities([]);
      } else {
        if (resp.error === "BAD_REQUEST")
          errorToast("Error! Use a different group name.");
      }
    } else {
      errorToast("Error!");
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup.groupId) {
      errorToast("Error!");
      return;
    }
    const req = {
      groupId: selectedGroup.groupId,
    };
    const resp = await deleteGroup(sendAndWaitWs, req);
    if (resp) {
      successToast("Deleted");
      handleLoadGroups();
      setGroupDialogOpen(false);
      setGroupName("");
      setSelectedEntities([]);
    } else {
      errorToast("Error!");
    }
  };

  const sortedGroups = groupData
    .filter((group) => {
      if (!groupFilterText) return true;
      const searchTerms = groupFilterText
        .toLowerCase()
        .split(" ")
        .filter((term) => term.length > 0);
      const searchableText = group.name.toLowerCase();
      return searchTerms.every((term) => searchableText.includes(term));
    })
    .sort((a, b) => {
      if (groupSortBy === "alphabet") {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aIsLetter = /^[a-zA-Z]/.test(aName);
        const bIsLetter = /^[a-zA-Z]/.test(bName);
        if (aIsLetter && !bIsLetter) return -1;
        if (!aIsLetter && bIsLetter) return 1;
        return aName.localeCompare(bName);
      }
      return 0;
    });

  return (
    <div>
      {showFilter && (
        <GroupFilter
          sortBy={groupSortBy}
          setSortBy={setGroupSortBy}
          setFilterText={setGroupFilterText}
        />
      )}
      <ResponsiveCardGrid spacing={1.5} breakpoints={{ xs: 2, sm: 3, md: 4 }}>
        <CreateGroupCard onCreateClick={() => handleEditGroup()} />
        {sortedGroups.map((group, index) => (
          <GroupCard
            key={index}
            group={group}
            index={index}
            getComponentBgColor={getComponentBgColor}
            onEditClick={handleEditGroup}
          />
        ))}
      </ResponsiveCardGrid>
      <EditGroupDialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        onSave={handleSaveGroup}
        onDelete={handleDeleteGroup}
        groupName={groupName}
        setGroupName={setGroupName}
        entities={entityData}
        selectedEntities={selectedEntities}
        onToggleEntity={handleToggleEntity}
        isNewGroup={newGroup}
      />
    </div>
  );
};

export default GroupListView;
